/*
 * Gift card page currency switch (Phase 3 plan 02).
 *
 * D-01/D-02: currency is a free choice on the page and quietly switches the
 *   visit's market through the theme's own localization form, landing back on
 *   this same page.
 * D-03: SGD/HKD toggle buttons (aria-pressed); the switch happens on activation (click, Enter,
 *   Space), never on focus. Review fix WR-09 replaced the former radios.
 * D-04: with items in the cart a confirmation dialog comes first; Cancel sends nothing.
 * D-05: a gift card of the other currency in the cart shows a "separate orders" note.
 * D-06: amount, occasion, decoration and delivery mode travel across the switch in
 *   the return_to query (gc_amt, gc_occ, gc_des, gc_to); gc_des is the decoration
 *   handle (D-07), checked against the page's enabled decoration radios; recipient details travel
 *   only through sessionStorage ('belantara-gc-recipient'), read once and deleted,
 *   never in the URL.
 *
 * Everything read from the URL or storage is untrusted: values are checked against
 * the page model and applied with .checked / .value / textContent only.
 */
(() => {
  const root = document.querySelector('[data-gc-page]');
  const box = root ? root.querySelector('[data-gc-currency-root]') : null;
  if (!root || !box) return;

  let copyCache = null;
  function gcCopy(key, vars) {
    if (!copyCache) {
      try {
        copyCache = JSON.parse(root.querySelector('[data-gc-copy]')?.textContent || '{}');
      } catch (error) {
        copyCache = {};
      }
    }
    const text = copyCache[key];
    if (typeof text !== 'string') return key;
    return text.replace(/\{(\w+)\}/g, (match, name) => (vars && name in vars ? String(vars[name]) : match));
  }

  const STORAGE_KEY = 'belantara-gc-recipient';
  const GC_PARAMS = ['gc_amt', 'gc_occ', 'gc_des', 'gc_to'];
  const TIMEOUT_MS = 8000;
  const MAX_AGE_MS = 60000;
  // Recipient details are restored only on a load that came back from our own switch
  // (it carries gc_* params). Captured before restoreChoices strips them.
  const cameFromSwitch = GC_PARAMS.some((name) => new URLSearchParams(window.location.search).has(name));

  const form = box.querySelector('form');
  const buttons = Array.from(box.querySelectorAll('button[data-currency]'));
  const dialog = box.querySelector('[data-gc-switch-dialog]');
  const errorLine = box.querySelector('[data-gc-currency-error]');
  const mixedNote = box.querySelector('[data-gc-mixed-note]');
  const cartUrl = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart.js';
  let pending = false;
  let timer = null;
  let activated = null;

  const productForm = () => root.querySelector('[data-gc-product-form] form');
  const addButton = () => root.querySelector('[data-gc-product-form] button[type="submit"]');

  function readModel() {
    try {
      return JSON.parse(root.querySelector('[data-gc-model]')?.textContent || 'null');
    } catch (error) {
      return null;
    }
  }

  function clean(value, max) {
    return typeof value === 'string' ? value.slice(0, max) : null;
  }

  function applyRecipient(data) {
    const target = productForm();
    if (!target) return;
    const values = {
      'properties[Recipient name]': data.name,
      'properties[Recipient email]': data.email,
      'properties[Message]': data.message,
      'properties[Send on]': data.sendOn,
    };
    Object.keys(values).forEach((name) => {
      const field = target.elements[name];
      if (!field || values[name] === null) return;
      field.value = values[name];
      field.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }

  function readRecipient() {
    let raw = null;
    try {
      raw = sessionStorage.getItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      return null;
    }
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      if (typeof parsed.at !== 'number' || Date.now() - parsed.at > MAX_AGE_MS || parsed.at > Date.now()) return null;
      const sendOn =
        typeof parsed.sendOn === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(parsed.sendOn) ? parsed.sendOn : null;
      return {
        name: clean(parsed.name, 100),
        email: clean(parsed.email, 138),
        message: clean(parsed.message, 200),
        sendOn,
      };
    } catch (error) {
      return null;
    }
  }

  function restoreChoices() {
    const params = new URLSearchParams(window.location.search);
    const current = typeof root.getSelection === 'function' ? root.getSelection() : null;
    const model = readModel();

    if (current && model) {
      const wanted = {};
      const amt = params.get('gc_amt');
      if (amt !== null && /^[0-9]$/.test(amt) && Number(amt) < (model.amounts || []).length) {
        wanted.amountIndex = Number(amt);
      }
      const occasion = (model.occasions || []).find((item) => item.handle === params.get('gc_occ'));
      if (occasion) wanted.occasion = occasion.handle;
      // gc_des carries the decoration handle (D-07). It counts only when it is an enabled radio on
      // this page; a hidden, unknown or unavailable handle falls back to the default decoration.
      if (params.has('gc_des')) {
        const handle = params.get('gc_des');
        const radio = Array.from(root.querySelectorAll('input[name="gc-decoration"]')).find(
          (item) => item.value === handle,
        );
        wanted.decoration = radio && !radio.disabled ? handle : model.defaultDecoration;
      }
      if (Object.keys(wanted).length > 0) {
        root.select({ ...current, ...wanted }, { announce: false });
      }
      const to = params.get('gc_to');
      if (to === 'send' || to === 'keep') {
        const radio = root.querySelector(`input[name="gc-delivery"][value="${to}"]`);
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    // Strip only our own params; others (preview_theme_id, ...) stay.
    if (GC_PARAMS.some((name) => params.has(name))) {
      GC_PARAMS.forEach((name) => params.delete(name));
      const rest = params.toString();
      history.replaceState(
        history.state,
        '',
        window.location.pathname + (rest ? '?' + rest : '') + window.location.hash,
      );
    }
  }

  function restoreRecipient() {
    const data = readRecipient();
    if (!data || !cameFromSwitch || !current()) return;
    if (root.dataset.gcRecipientReady === 'true') {
      applyRecipient(data);
    } else {
      root.addEventListener('gc:recipient-ready', () => applyRecipient(data), { once: true });
    }
  }

  function current() {
    return typeof root.getSelection === 'function' ? root.getSelection() : null;
  }

  function currencyOf(item) {
    const option = item.variant_options && item.variant_options[0];
    const text = typeof option === 'string' ? option : item.variant_title;
    return typeof text === 'string' ? text.trim().split(/\s+/)[0] : '';
  }

  function checkMixed() {
    if (!mixedNote) return;
    fetch(cartUrl, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        const mixed = (cart.items || []).some((item) => {
          if (item.gift_card !== true) return false;
          const cur = currencyOf(item);
          return (cur === 'SGD' || cur === 'HKD') && cur !== root.dataset.currency;
        });
        if (mixed) mixedNote.hidden = false;
      })
      .catch(() => {});
  }

  function setPending(on) {
    pending = on;
    buttons.forEach((button) => {
      if (on) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    });
    const add = addButton();
    if (add) {
      add.disabled = on;
      if (on) add.setAttribute('aria-busy', 'true');
      else add.removeAttribute('aria-busy');
    }
  }

  function saveRecipient() {
    const target = productForm();
    const mode = root.querySelector('input[name="gc-delivery"]:checked');
    if (!target || !mode || mode.value !== 'send') return;
    const get = (name) => (target.elements[name] ? String(target.elements[name].value) : '');
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          name: get('properties[Recipient name]'),
          email: get('properties[Recipient email]'),
          message: get('properties[Message]'),
          sendOn: get('properties[Send on]'),
          at: Date.now(),
        }),
      );
    } catch (error) {
      // Storage unavailable: the switch still works, details are re-entered.
    }
  }

  function switchTo() {
    setPending(true);
    saveRecipient();

    const q = new URLSearchParams();
    const selection = current();
    if (selection) {
      q.set('gc_amt', String(selection.amountIndex));
      q.set('gc_occ', selection.occasion);
      if (selection.decoration) q.set('gc_des', selection.decoration);
    }
    const mode = root.querySelector('input[name="gc-delivery"]:checked');
    if (mode) q.set('gc_to', mode.value);

    // The chosen country travels in a hidden input set just before submit.
    let country = form.elements.country_code;
    if (!country || country.type !== 'hidden') {
      country = document.createElement('input');
      country.type = 'hidden';
      country.name = 'country_code';
      form.appendChild(country);
    }
    country.value = activated ? activated.dataset.country || '' : '';

    let returnTo = form.elements.return_to;
    if (!returnTo) {
      returnTo = document.createElement('input');
      returnTo.type = 'hidden';
      returnTo.name = 'return_to';
      form.appendChild(returnTo);
    }
    // The path comes from the page itself, never from a parameter (open redirect guard).
    returnTo.value = window.location.pathname + (q.toString() ? '?' + q.toString() : '');

    window.addEventListener('pagehide', () => clearTimeout(timer), { once: true });
    timer = setTimeout(() => {
      setPending(false);
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        // Nothing to clean up if storage is unavailable.
      }
      if (errorLine) {
        errorLine.textContent = gcCopy('currency_error');
        errorLine.hidden = false;
      }
    }, TIMEOUT_MS);

    form.requestSubmit();
  }

  function cancelSwitch() {
    // The pressed state only changes once the new page has loaded; just release the lock.
    setPending(false);
    if (activated) activated.focus();
  }

  function onChoose(button) {
    activated = button;
    // Lock at once so a second activation during the cart check cannot start another switch.
    setPending(true);
    if (errorLine) errorLine.hidden = true;
    const currency = button.dataset.currency;
    fetch(cartUrl, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => cart.item_count > 0)
      .catch(() => true)
      .then((hasItems) => {
        if (!hasItems || !dialog) {
          switchTo();
          return;
        }
        dialog.querySelector('[data-gc-dialog-title]').textContent = gcCopy('dialog_title', { currency });
        dialog.querySelector('[data-gc-dialog-body]').textContent = gcCopy('dialog_body', { currency });
        dialog.showModal();
        const cancel = dialog.querySelector('.gc-dialog__cancel');
        if (cancel) cancel.focus();
      });
  }

  function init() {
    // Back button: the browser may restore this document from bfcache with the
    // switch still pending (pagehide cleared the timer). Undo the pending state.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && pending) {
        clearTimeout(timer);
        setPending(false);
      }
    });

    restoreChoices();
    restoreRecipient();
    checkMixed();

    // Activation only (click, Enter, Space on a native button). Moving focus never switches.
    // Pressing the already-pressed button, or any button while a switch is pending, does nothing.
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        if (pending || button.getAttribute('aria-pressed') === 'true') return;
        if (button.dataset.currency === root.dataset.currency) return;
        onChoose(button);
      });
    });

    if (dialog) {
      dialog.addEventListener('cancel', cancelSwitch);
      const cancel = dialog.querySelector('.gc-dialog__cancel');
      const proceed = dialog.querySelector('.gc-dialog__continue');
      if (cancel) {
        cancel.addEventListener('click', () => {
          dialog.close();
          cancelSwitch();
        });
      }
      if (proceed) {
        proceed.addEventListener('click', () => {
          dialog.close();
          switchTo();
        });
      }
    }
  }

  customElements.whenDefined('gift-card-page').then(init);
})();
